import { useState, useEffect, useCallback } from 'react';
import './ParkingTable.css';
import ReservationEditForm from './ReservationEditForm';
import { useSSE, getWorkerUrl } from '../utils/useSSE';
import { getApiUrl } from '../utils/api';

const DAYS_TO_SHOW = 11; // Today + next 10 days

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    // Get all accounts and use the first one
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    console.log('Getting ID token for account:', account);
    // Try to get token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    console.log('ID token response:', response);
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

function ParkingTable({ user, powerUserMode, msalInstance }) {
  const [reservations, setReservations] = useState({});
  const [parkingSlots, setParkingSlots] = useState([]);
  const [allParkingSlots, setAllParkingSlots] = useState([]); // Store all slots for lookups
  const [workingDays, setWorkingDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [editingSlotLabel, setEditingSlotLabel] = useState(null);

  // Get current date in Finland timezone
  const getTodayInFinland = () => {
    const now = new Date();
    // Convert to Finland timezone (Europe/Helsinki)
    const finlandDateStr = now.toLocaleString('en-US', {
      timeZone: 'Europe/Helsinki',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // Parse MM/DD/YYYY format
    const [month, day, year] = finlandDateStr.split(',')[0].split('/');
    const finlandDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    return finlandDate;
  };

  // Calculate working days (Monday-Friday) starting from today in Finland timezone
  const calculateWorkingDays = () => {
    const days = [];
    let current = getTodayInFinland();

    while (days.length < DAYS_TO_SHOW) {
      const dayOfWeek = current.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Initialize working days
  useEffect(() => {
    const updateDays = () => {
      setWorkingDays(calculateWorkingDays());
    };

    updateDays();

    // Check for midnight to refresh days
    const now = new Date();
    const midnight = new Date(now);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);
    const msUntilMidnight = midnight - now;

    const midnightTimer = setTimeout(() => {
      updateDays();
      // Set up daily interval after first midnight
      const dailyInterval = setInterval(updateDays, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(midnightTimer);
  }, []);

  // Load parking slots from API (only on initial load and when window becomes active)
  useEffect(() => {
    let isInitialLoad = true;

    const loadParkingSlots = async (showLoading = false) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        const idToken = await getIdToken(msalInstance);
        if (!idToken) {
          throw new Error('Failed to get authentication token');
        }

        const response = await fetch(getApiUrl('/api/parkingSlots'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load parking slots: ${response.statusText}`);
        }

        const data = await response.json();
        // Store all slots for lookups (e.g., reservation notification)
        setAllParkingSlots(data);
        // Filter to only available slots and sort by display_order
        const availableSlots = data
          .filter((slot) => slot.available === 1)
          .sort((a, b) => a.display_order - b.display_order);
        setParkingSlots(availableSlots);
      } catch (e) {
        console.error('Failed to load parking slots:', e);
        if (showLoading) {
          setError('Failed to load parking slots. Please refresh the page.');
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    };

    // Load immediately with loading state
    loadParkingSlots(true);

    // Handle visibility change to refresh when window becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && !isInitialLoad) {
        // Refresh parking slots when window becomes active (show loading spinner)
        loadParkingSlots(true);
      }
      isInitialLoad = false;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load reservations from API
  const loadReservations = async () => {
    try {
      // Get ID token for authentication
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        console.error('Unable to get ID token for authentication');
        setError('Authentication error. Please refresh the page.');
        return;
      }

      const response = await fetch(getApiUrl('/api/reservations'), {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load reservations: ${response.statusText}`);
      }

      const data = await response.json();

      // Only update if data has changed
      setReservations((prevReservations) => {
        const prevJson = JSON.stringify(prevReservations);
        const newJson = JSON.stringify(data);
        if (prevJson !== newJson) {
          return data;
        }
        return prevReservations;
      });
    } catch (e) {
      console.error('Failed to load reservations:', e);
      setError(
        (prevError) => prevError || 'Failed to load reservations. Please refresh the page.'
      );
    }
  };

  // Load reservations on initial mount
  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance]);

  // Connect to SSE for real-time updates
  const workerUrl = getWorkerUrl();
  const [sseError, setSseError] = useState(false);
  
  const handleSSEMessage = useCallback((eventData) => {
    // Handle SSE events - use received data directly instead of fetching
    if (eventData.type === 'reservations' && eventData.data?.data) {
      // Update reservations directly with received data
      setReservations(eventData.data.data);
    } else if (eventData.type === 'parkingSlots' && eventData.data?.data) {
      // Update parking slots directly with received data
      const slotsData = eventData.data.data;
      setAllParkingSlots(slotsData);
      const availableSlots = slotsData
        .filter((slot) => slot.available === 1)
        .sort((a, b) => a.display_order - b.display_order);
      setParkingSlots(availableSlots);
    }
  }, []); // State setters are stable

  const handleSSEError = useCallback((error) => {
    console.error('SSE connection error:', error);
    setSseError(true);
  }, []);

  useSSE(workerUrl, handleSSEMessage, handleSSEError);

  // Fallback to polling if SSE fails or is not configured
  useEffect(() => {
    if (!workerUrl || sseError) {
      console.log('Using polling fallback for reservations');
      const intervalId = setInterval(() => {
        loadReservations();
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [workerUrl, sseError]); // loadReservations is stable, no need to include

  // Create or delete reservation via API
  const updateReservation = async (action, date, slotLabel) => {
    try {
      // Get ID token for authentication
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error('Unable to get ID token for authentication');
      }

      const response = await fetch(getApiUrl('/api/reservations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action,
          date: formatDateKey(date),
          slot_id: slotLabel,
          // Note: user info is now extracted from token server-side, not sent in body
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update reservation');
      }

      // Reload reservations after successful update
      const reloadIdToken = await getIdToken(msalInstance);
      if (reloadIdToken) {
        const reloadResponse = await fetch(getApiUrl('/api/reservations'), {
          headers: {
            Authorization: `Bearer ${reloadIdToken}`,
          },
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setReservations(data);
        }
      }

      return result;
    } catch (e) {
      console.error('Failed to update reservation:', e);
      throw e;
    }
  };

  // Format date as YYYY-MM-DD (using local timezone)
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}.${month}.`;
  };

  // Get weekday name
  const getWeekday = (date) => {
    const days = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'];
    return days[date.getDay()];
  };

  // Check if user has multiple reservations for this day
  const getUserReservationsForDay = (dateKey) => {
    if (!reservations[dateKey]) return [];
    // Get all reservations for this user on this day
    return Object.values(reservations[dateKey]).filter(
      (reservation) => reservation.user_id === user.user_id
    );
  };

  // Check if user has multiple reservations for this day
  const hasMultipleReservationsForDay = (dateKey) => {
    return getUserReservationsForDay(dateKey).length > 1;
  };

  // Handle slot click
  const handleSlotClick = async (date, slotLabel) => {
    const dateKey = formatDateKey(date);
    const currentReservation = reservations[dateKey]?.[slotLabel];

    // If PowerUser mode is enabled, open edit form for any slot
    if (user.isPowerUser && powerUserMode) {
      setEditingReservation(currentReservation || null);
      setEditingDate(date);
      setEditingSlotLabel(slotLabel);
      return;
    }

    // Regular behavior for non-PowerUser or PowerUser mode OFF
    // Check if slot is taken
    if (currentReservation) {
      const isOwnReservation = currentReservation.user_id === user.user_id;

      // If it's the user's own reservation, allow cancellation
      if (isOwnReservation) {
        try {
          await updateReservation('delete', date, slotLabel);
        } catch (e) {
          alert(`Failed to cancel reservation: ${e.message}`);
        }
        return;
      }

      // Regular user trying to take someone else's slot
      alert(`This slot is reserved by ${currentReservation.family_name}`);
      return;
    }

    // Check if user already has a reservation for this day and prompt for confirmation
    const existingReservations = getUserReservationsForDay(dateKey);
    if (existingReservations.length > 0) {
      // Find slot labels for existing reservations
      const existingSlots = [];
      for (const [slotLabel, reservation] of Object.entries(reservations[dateKey])) {
        if (reservation.user_id === user.user_id) {
          existingSlots.push(slotLabel);
        }
      }
      
      const existingSlotsText = existingSlots.join(', ');
      const confirmed = window.confirm(
        `You already have a reservation for ${formatDate(date)} (${existingSlotsText}).\n\n` +
        `Do you want to book an additional slot (${slotLabel}) for the same day?`
      );
      
      if (!confirmed) {
        return;
      }
    }

    // Make reservation
    try {
      await updateReservation('create', date, slotLabel);
    } catch (e) {
      alert(`Failed to create reservation: ${e.message}`);
    }
  };

  // Reload reservations after save
  const handleReservationSave = async () => {
    try {
      const idToken = await getIdToken(msalInstance);
      if (idToken) {
        const reloadResponse = await fetch(getApiUrl('/api/reservations'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setReservations(data);
        }
      }
    } catch (e) {
      console.error('Failed to reload reservations:', e);
    }
  };

  // Get slot status
  const getSlotStatus = (date, slotLabel) => {
    const dateKey = formatDateKey(date);
    const reservation = reservations[dateKey]?.[slotLabel];

    if (!reservation) {
      // Check if user has multiple reservations on this day (to show red for all)
      const hasMultiple = hasMultipleReservationsForDay(dateKey);
      return { status: 'available', owner: null, hasMultiple };
    }
    
    if (reservation.user_id === user.user_id) {
      // Check if user has multiple reservations on this day
      const hasMultiple = hasMultipleReservationsForDay(dateKey);
      return { 
        status: hasMultiple ? 'multiple-mine' : 'mine', 
        owner: reservation.family_name,
        hasMultiple 
      };
    }
    
    return { status: 'taken', owner: reservation.family_name };
  };

  // Format location for display
  const formatLocation = (location) => {
    if (location === 'ground_floor') return 'Ground Floor';
    if (location === 'basement') return 'Basement';
    return location;
  };

  // Get today's reservation for the current user
  const getTodayReservation = () => {
    const todayKey = formatDateKey(getTodayInFinland());
    if (!reservations[todayKey]) return null;

    // Find the user's reservation for today
    for (const [slotLabel, reservation] of Object.entries(reservations[todayKey])) {
      if (reservation.user_id === user.user_id) {
        // Find the slot details from all slots (not just available ones)
        const slot = allParkingSlots.find((s) => s.label === slotLabel);
        return {
          slotLabel,
          location: slot?.location || 'Unknown',
          reservation,
        };
      }
    }
    return null;
  };

  const todayReservation = getTodayReservation();

  if (loading) {
    return (
      <div className="parking-table-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading parking slots...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parking-table-container">
        <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>{error}</div>
      </div>
    );
  }

  if (parkingSlots.length === 0) {
    return (
      <div className="parking-table-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>No parking slots available.</div>
      </div>
    );
  }

  return (
    <div className="parking-table-container">
      {editingDate && editingSlotLabel && (
        <ReservationEditForm
          reservation={editingReservation}
          date={editingDate}
          slotLabel={editingSlotLabel}
          parkingSlots={allParkingSlots}
          onClose={() => {
            setEditingReservation(null);
            setEditingDate(null);
            setEditingSlotLabel(null);
          }}
          onSave={handleReservationSave}
          msalInstance={msalInstance}
        />
      )}
      {todayReservation && (
        <div className="reservation-notification">
          <div className="reservation-notification-icon">📅</div>
          <div className="reservation-notification-content">
            <div className="reservation-notification-title">Your Reservation Today</div>
            <div className="reservation-notification-details">
              <span className="reservation-slot-label">{todayReservation.slotLabel}</span>
              <span className="reservation-location">
                {formatLocation(todayReservation.location)}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="parking-table-wrapper">
        <table className="parking-table">
          <thead>
            <tr>
              <th className="date-column">Date</th>
              {parkingSlots.map((slot, index) => (
                <th key={slot.id} className="slot-column">
                  <div className="slot-header-label">
                    {slot.label}
                    {slot.ev_charging === 1 && (
                      <span className="ev-icon" title="EV Charging Available">
                        ⚡
                      </span>
                    )}
                  </div>
                  <div className="slot-header-location">{formatLocation(slot.location)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workingDays.map((date, rowIndex) => {
              const dateKey = formatDateKey(date);
              const hasMultiple = hasMultipleReservationsForDay(dateKey);

              return (
                <tr key={dateKey}>
                  <td className="date-column">
                    <div className="date-display">{formatDate(date)}</div>
                    <div className="weekday-display">{getWeekday(date)}</div>
                  </td>
                  {parkingSlots.map((slot) => {
                    const { status, owner, hasMultiple: slotHasMultiple } = getSlotStatus(date, slot.label);
                    const currentReservation = reservations[dateKey]?.[slot.label];
                    const isTakenByOther = currentReservation && currentReservation.user_id !== user.user_id;
                    const isEmpty = !currentReservation;
                    // PowerUsers can click any slot (empty or taken by others) when PowerUser mode is enabled
                    const isPowerUserClickable = user.isPowerUser && powerUserMode && (isEmpty || isTakenByOther);
                    const slotClassName = `slot ${status}${isPowerUserClickable ? ' clickable' : ''}`;

                    return (
                      <td
                        key={slot.id}
                        className={slotClassName}
                        onClick={() => handleSlotClick(date, slot.label)}
                        title={
                          owner 
                            ? (hasMultiple ? `${owner} (Multiple bookings)` : owner)
                            : (isPowerUserClickable
                                ? 'Click to create/edit reservation (PowerUser)'
                                : (hasMultiple && status === 'available'
                                    ? 'Available (You have multiple bookings this day)'
                                    : 'Available'))
                        }
                      >
                        {owner || ''}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="legend">
        <div className="legend-item">
          <span className="legend-color available"></span>
          <span>Available</span>
        </div>
        <div className="legend-item">
          <span className="legend-color mine"></span>
          <span>Your Reservation</span>
        </div>
        <div className="legend-item">
          <span className="legend-color taken"></span>
          <span>Reserved</span>
        </div>
        <div className="legend-item">
          <span className="legend-color multiple-mine"></span>
          <span>Multiple Bookings</span>
        </div>
      </div>
    </div>
  );
}

export default ParkingTable;
